import type { RemoteThreadListAdapter, ThreadMessage } from '@assistant-ui/core';
import { createAssistantStream } from 'assistant-stream';

async function fetchThreadsApi(path: string, options?: RequestInit) {
	const res = await fetch(`/api/chat/threads${path}`, {
		headers: { 'Content-Type': 'application/json' },
		...options,
	});
	if (!res.ok) {
		throw new Error(`Thread API error: ${res.status}`);
	}
	return res.json();
}

function generateSimpleTitle(messages: readonly ThreadMessage[]): string {
	const firstUserMessage = messages.find((m) => m.role === 'user');
	if (!firstUserMessage) return 'New Chat';
	const textPart = firstUserMessage.content.find((p) => p.type === 'text');
	if (!textPart || textPart.type !== 'text') return 'New Chat';
	const text = textPart.text.trim();
	return text.length > 50 ? `${text.slice(0, 47)}...` : text;
}

export const mastraThreadListAdapter: RemoteThreadListAdapter = {
	async list() {
		return fetchThreadsApi('');
	},

	async initialize(threadId: string) {
		return fetchThreadsApi('', {
			body: JSON.stringify({ threadId }),
			method: 'POST',
		});
	},

	async rename(remoteId: string, newTitle: string) {
		await fetchThreadsApi(`?threadId=${encodeURIComponent(remoteId)}`, {
			body: JSON.stringify({ title: newTitle }),
			method: 'PATCH',
		});
	},

	async archive(remoteId: string) {
		await fetchThreadsApi(`?threadId=${encodeURIComponent(remoteId)}`, {
			body: JSON.stringify({ status: 'archived' }),
			method: 'PATCH',
		});
	},

	async unarchive(remoteId: string) {
		await fetchThreadsApi(`?threadId=${encodeURIComponent(remoteId)}`, {
			body: JSON.stringify({ status: 'regular' }),
			method: 'PATCH',
		});
	},

	async delete(remoteId: string) {
		await fetchThreadsApi(`?threadId=${encodeURIComponent(remoteId)}`, {
			method: 'DELETE',
		});
	},

	async fetch(threadId: string) {
		return fetchThreadsApi(`/${encodeURIComponent(threadId)}`);
	},

	async generateTitle(remoteId: string, messages: readonly ThreadMessage[]) {
		const title = generateSimpleTitle(messages);
		await fetchThreadsApi(`?threadId=${encodeURIComponent(remoteId)}`, {
			body: JSON.stringify({ title }),
			method: 'PATCH',
		}).catch(() => {
			// Thread may not exist yet — title will be set on next successful call
		});
		return createAssistantStream((controller) => {
			controller.appendText(title);
			controller.close();
		});
	},
};
